import { Component, input, linkedSignal, model, output } from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  FormsModule,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { InputNumberModule } from 'primeng/inputnumber';
import { SelectModule } from 'primeng/select';
import { SelectOption } from '../form-field/form-field.component';
import { LatLngModel } from './lat-long.model';

@Component({
  selector: 're-lat-long-template',
  templateUrl: './lat-long-template.component.html',
  styleUrl: './lat-long-template.component.css',
  imports: [InputNumberModule, SelectModule, FormsModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: LatLongTemplateComponent,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: LatLongTemplateComponent,
      multi: true,
    },
  ],
})
export class LatLongTemplateComponent implements ControlValueAccessor {
  isInvalid = true;
  required = input<boolean>(false);
  name = input<string>();
  title = input<string>();
  latLng = model<LatLngModel>();
  editableLatLng = linkedSignal(() => this.latLng());

  id = input<string>();
  labelWidthPercentage = input<number>(25);
  valueChange = output<LatLngModel>();

  writeValue(): void {
    this.latLng.set(this.editableLatLng());
  }

  registerOnChange(fn: (value: LatLngModel) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function, @typescript-eslint/no-unused-vars
  onChange = (value: LatLngModel) => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onTouched = () => {};

  fieldTouched() {
    this.onTouched();
  }

  validate({ value }: FormControl<LatLngModel>) {
    if (!value) {
      return {
        invalid: true,
      };
    }

    const errors: string[] = [];
    const required = this.required();

    const isSelectOption = (value: unknown): value is SelectOption => {
      if (!value) return false;
      return (
        Object.prototype.hasOwnProperty.call(value, 'code') &&
        Object.prototype.hasOwnProperty.call(value, 'name')
      );
    };

    const addErrors = (
      id: keyof LatLngModel,
      min: number,
      max: number,
      validDirections: string[] = [],
    ) => {
      const field = value[id];
      if (required && field == null) errors.push(`${id} is required`);

      if (isSelectOption(field)) {
        addDirectionError(id, field, validDirections);
      } else {
        addNumberError(id, field, min, max);
      }
    };

    const addNumberError = (
      id: string,
      value: number | undefined,
      min: number,
      max: number,
    ) => {
      if (value == null) return;
      if (isNaN(value)) errors.push(`${id} must be a number`);
      if (value < min)
        errors.push(`${id} must be greater than or equal to ${min}`);
      if (value > max)
        errors.push(`${id} must be less than or equal to ${max}`);
    };

    const addDirectionError = (
      id: string,
      field: SelectOption,
      validDirections: string[],
    ) => {
      if (
        !validDirections.includes(field.name) ||
        !validDirections.includes(field.code as string)
      )
        errors.push(`${id} must be one of: ${validDirections.join(',')}`);
    };

    addErrors('latDegrees', 0, 90);
    addErrors('latMinutes', 0, 60);
    addErrors('latSeconds', 0, 60);
    addErrors('latDirection', 0, 0, this.validLatDirections);

    addErrors('longDegrees', 0, 180);
    addErrors('longMinutes', 0, 60);
    addErrors('longSeconds', 0, 60);
    addErrors('longDirection', 0, 0, this.validLongDirections);

    if (errors.length === 0) return null;
    return Object.fromEntries(errors.map((k) => [k, true]));
  }

  updateLatLng(latLng: Partial<LatLngModel>) {
    this.editableLatLng.update((state) => {
      const updatedState = { ...state, ...latLng };
      this.valueChange.emit(updatedState);
      return updatedState;
    });
    const change = this.editableLatLng();
    if (!change) return;

    this.onChange(change);
  }

  LAT_OPTIONS: SelectOption[] = [
    { name: 'N', code: 'N' },
    { name: 'S', code: 'S' },
  ] as const;
  LONG_OPTIONS: SelectOption[] = [
    { name: 'E', code: 'E' },
    { name: 'W', code: 'W' },
  ] as const;
  validLongDirections: string[] = this.LONG_OPTIONS.map((o) => o.name);
  validLatDirections: string[] = this.LAT_OPTIONS.map((o) => o.name);
}
